// Module Recherche Logement - Interface Candidat
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  FiSearch, FiMapPin, FiHome, FiDollarSign,
  FiMaximize, FiHeart, FiFilter, FiMap, FiAlertCircle, FiX, FiCheckCircle
} from 'react-icons/fi';
import { Housing } from '@/models';
import { HousingsServices } from '@/services/HousingServices';
import { CandidateServices } from '@/services/CandidateServices';

// Get API base URL for constructing full image URLs
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
const getFullImageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  // If it's already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If it starts with /media, construct full URL
  if (path.startsWith('/media')) {
    return `${API_BASE_URL.replace('/api/v1', '')}${path}`;
  }
  // Otherwise assume it's a relative path
  return `${API_BASE_URL.replace('/api/v1', '')}/media/${path}`;
};

// Types pour les filtres
interface HousingFilters {
  type?: string;
  maxPrice?: number;
  minSurface?: number;
  status?: string;
  city?: string;
}

// Styled Components
const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const SearchSection = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.xl};
`;

const SearchBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const SearchInput = styled.input`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-family: ${theme.typography.fontFamily};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}20;
    color: ${theme.colors.primary};
  }
`;

const SearchButton = styled(FilterButton)`
  background: ${theme.colors.primary};
  color: white;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FiltersRow = styled.div<{ visible: boolean }>`
  display: ${props => props.visible ? 'grid' : 'none'};
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.surface};
`;

const FilterGroup = styled.div``;

const FilterLabel = styled.label`
  display: block;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-family: ${theme.typography.fontFamily};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const ResultsCount = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const ViewToggle = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ViewButton = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => props.active ? theme.colors.primary : theme.colors.surface};
  color: ${props => props.active ? 'white' : theme.colors.text};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.active ? theme.colors.primary : theme.colors.primary}20;
  }
`;

const HousingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing.lg};
`;

const HousingCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
  transition: all ${theme.transitions.normal};
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const HousingImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const AvailableBadge = styled.div<{ available: boolean }>`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${props => props.available ? theme.colors.success : theme.colors.error};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
`;

const FavoriteButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  left: ${theme.spacing.md};
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    transform: scale(1.1);
    color: ${theme.colors.error};
  }
`;

const HousingContent = styled.div`
  padding: ${theme.spacing.lg};
`;

const HousingTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const HousingLocation = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  color: ${theme.colors.text}60;
  font-size: ${theme.typography.sizes.sm};
  margin-bottom: ${theme.spacing.md};
`;

const HousingDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.md} 0;
  border-top: 1px solid ${theme.colors.surface};
  border-bottom: 1px solid ${theme.colors.surface};
`;

const Detail = styled.div`
  text-align: center;
`;

const DetailValue = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
`;

const DetailLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-top: ${theme.spacing.xs};
`;

const HousingPrice = styled.div`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.success};
  text-align: center;
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  margin-top: ${theme.spacing.md};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${theme.colors.primary}80;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.sizes.lg};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}10;
  border: 1px solid ${theme.colors.error}40;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  color: ${theme.colors.error};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']};
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: ${theme.spacing.lg};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  background: white;
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  background: ${theme.colors.surface};
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.error}20;
    color: ${theme.colors.error};
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    transform: scale(1.05);
    box-shadow: ${theme.shadows.lg};
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const DetailSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const DetailTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const DetailItemLabel = styled.span`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  font-weight: ${theme.typography.weights.medium};
`;

const DetailItemValue = styled.span`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  font-weight: ${theme.typography.weights.semibold};
`;

const FeaturesList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.sm};
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
`;

const DescriptionText = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  line-height: 1.6;
  margin: 0;
`;

const ModalFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: white;
  padding: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
`;

const PriceDisplay = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.success};
`;

const ApplyButtonLarge = styled(ApplyButton)`
  width: auto;
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  margin: 0;
`;

export const HousingSearch: React.FC = () => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [housings, setHousings] = useState<Housing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<HousingFilters>({});
  const [applying, setApplying] = useState<number | null>(null); // ID du logement en cours de postulation
  const [selectedHousing, setSelectedHousing] = useState<Housing | null>(null); // Pour le modal de détails

  // Charger les logements au montage du composant
  useEffect(() => {
    loadHousings();
  }, []);

  const loadHousings = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        status: 'available' // Seulement les logements disponibles
      };

      const response = await HousingsServices.list(params);
      const result = response as { success: boolean; data?: Housing[] };

      if (result.success && result.data) {
        setHousings(result.data);
      } else {
        setHousings([]);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des logements:', err);
      setError('Impossible de charger les logements. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        status: 'available'
      };

      // Ajouter la recherche par texte
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Ajouter les filtres
      if (filters.type) {
        params.type = filters.type;
      }
      if (filters.maxPrice) {
        params.max_rent = filters.maxPrice;
      }
      if (filters.minSurface) {
        params.min_surface = filters.minSurface;
      }
      if (filters.city) {
        params.city = filters.city;
      }

      const response = await HousingsServices.list(params);
      const result = response as { success: boolean; data?: Housing[] };

      if (result.success && result.data) {
        setHousings(result.data);
      } else {
        setHousings([]);
      }
    } catch (err: any) {
      console.error('Erreur lors de la recherche:', err);
      setError('Erreur lors de la recherche. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof HousingFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const getHousingTitle = (housing: Housing): string => {
    return `${housing.type || 'Logement'} - ${housing.city || ''}`;
  };

  const isHousingAvailable = (housing: Housing): boolean => {
    return housing.status === 'available';
  };

  const formatPrice = (price: number | null): string => {
    if (!price) return 'Prix non renseigné';
    return `${price.toLocaleString()} FCFA`;
  };

  const handleApply = async (housingId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher la navigation vers les détails

    try {
      setApplying(housingId);
      setError(null);

      // 1. Récupérer l'ID du candidat via getUserFolder
      const folderResponse = await CandidateServices.getUserFolder();
      const folderResult = folderResponse as {
        success: boolean;
        data?: {
          candidate: { id: number };
        };
      };

      if (!folderResult.success || !folderResult.data?.candidate?.id) {
        setError('Vous devez d\'abord compléter votre dossier de candidature avant de postuler.');
        return;
      }

      const candidateId = folderResult.data.candidate.id;

      console.log('CANDIDATE_ID:', candidateId);


      // 2. Postuler au logement
      const applyResponse = await HousingsServices.apply(housingId, {
        candidate_id: candidateId
      } as any);

      const applyResult = applyResponse as {
        success: boolean;
        message?: string;
      };

      if (applyResult.success) {
        alert('Candidature envoyée avec succès! Vous serez notifié du suivi de votre dossier.');
        // Optionnel: Rediriger vers la page de suivi des candidatures
        navigate('/candidate/applications');
      } else {
        setError(applyResult.message || 'Une erreur est survenue lors de la postulation.');
      }
    } catch (err: any) {
      console.error('Erreur lors de la postulation:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Impossible de postuler pour le moment. Veuillez réessayer.';
      setError(errorMessage);
    } finally {
      setApplying(null);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Recherche de Logement</PageTitle>
        <PageSubtitle>
          Trouvez le logement qui correspond à vos besoins
        </PageSubtitle>
      </PageHeader>

      {error && (
        <ErrorMessage>
          <FiAlertCircle size={24} />
          <div>{error}</div>
        </ErrorMessage>
      )}

      <SearchSection>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Rechercher par ville, code postal, type de logement..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <FilterButton onClick={() => setShowFilters(!showFilters)}>
            <FiFilter /> Filtres
          </FilterButton>
          <SearchButton onClick={handleSearch} disabled={loading}>
            <FiSearch /> {loading ? 'Recherche...' : 'Rechercher'}
          </SearchButton>
        </SearchBar>

        <FiltersRow visible={showFilters}>
          <FilterGroup>
            <FilterLabel>Type de logement</FilterLabel>
            <FilterSelect
              value={filters.type || ''}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">Tous</option>
              <option value="Studio">Studio</option>
              <option value="T1">T1</option>
              <option value="T2">T2</option>
              <option value="T3">T3</option>
              <option value="T4">T4</option>
              <option value="T5">T5+</option>
              <option value="Maison">Maison</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Prix maximum (FCFA)</FilterLabel>
            <FilterSelect
              value={filters.maxPrice || ''}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Tous</option>
              <option value="50000">50 000 FCFA</option>
              <option value="100000">100 000 FCFA</option>
              <option value="150000">150 000 FCFA</option>
              <option value="200000">200 000 FCFA</option>
              <option value="300000">300 000 FCFA</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Surface minimum (m²)</FilterLabel>
            <FilterSelect
              value={filters.minSurface || ''}
              onChange={(e) => handleFilterChange('minSurface', e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Tous</option>
              <option value="20">20 m²</option>
              <option value="40">40 m²</option>
              <option value="60">60 m²</option>
              <option value="80">80 m²</option>
              <option value="100">100 m²</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Statut</FilterLabel>
            <FilterSelect
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tous</option>
              <option value="available">Disponible</option>
              <option value="soon">Bientôt disponible</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersRow>
      </SearchSection>

      {loading ? (
        <LoadingMessage>Chargement des logements...</LoadingMessage>
      ) : housings.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>🏠</EmptyStateIcon>
          <EmptyStateTitle>Aucun logement trouvé</EmptyStateTitle>
          <EmptyStateText>
            Essayez de modifier vos critères de recherche ou revenez plus tard pour voir les nouvelles offres.
          </EmptyStateText>
        </EmptyState>
      ) : (
        <>
          <ResultsHeader>
            <ResultsCount>{housings.length} logement(s) trouvé(s)</ResultsCount>
            <ViewToggle>
              <ViewButton active title="Vue grille">
                <FiHome />
              </ViewButton>
              <ViewButton title="Vue carte">
                <FiMap />
              </ViewButton>
            </ViewToggle>
          </ResultsHeader>

          <HousingGrid>
            {housings.map(housing => (
              <HousingCard key={housing.id} onClick={() => setSelectedHousing(housing)}>
                <HousingImage>
                  {getFullImageUrl(housing.main_photo) ? (
                    <img
                      src={getFullImageUrl(housing.main_photo)!}
                      alt={getHousingTitle(housing)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        // Fallback to icon if image fails to load
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const icon = document.createElement('div');
                          icon.innerHTML = '<svg width="64" height="64" style="opacity: 0.3"><use href="#fi-home" /></svg>';
                          parent.appendChild(icon);
                        }
                      }}
                    />
                  ) : (
                    <FiHome size={64} style={{ opacity: 0.3 }} />
                  )}
                  <AvailableBadge available={isHousingAvailable(housing)}>
                    {isHousingAvailable(housing) ? 'Disponible' : 'Bientôt'}
                  </AvailableBadge>
                  <FavoriteButton onClick={(e) => e.stopPropagation()}>
                    <FiHeart />
                  </FavoriteButton>
                </HousingImage>

                <HousingContent>
                  <HousingTitle>{getHousingTitle(housing)}</HousingTitle>
                  <HousingLocation>
                    <FiMapPin />
                    {housing.address || 'Adresse non renseignée'}, {housing.city || ''}
                  </HousingLocation>

                  <HousingDetails>
                    <Detail>
                      <DetailValue>{housing.surface || '-'}</DetailValue>
                      <DetailLabel>m²</DetailLabel>
                    </Detail>
                    <Detail>
                      <DetailValue>{housing.rooms || '-'}</DetailValue>
                      <DetailLabel>pièces</DetailLabel>
                    </Detail>
                    <Detail>
                      <DetailValue>{housing.bedrooms || '-'}</DetailValue>
                      <DetailLabel>chambres</DetailLabel>
                    </Detail>
                  </HousingDetails>

                  <HousingPrice>
                    {formatPrice(housing.rent)}/mois
                  </HousingPrice>

                  {housing.charges && housing.charges > 0 && (
                    <div style={{
                      textAlign: 'center',
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginTop: '4px'
                    }}>
                      + {housing.charges.toLocaleString()} FCFA de charges
                    </div>
                  )}

                  <ApplyButton
                    onClick={(e) => handleApply(housing.id!, e)}
                    disabled={applying === housing.id}
                  >
                    {applying === housing.id ? 'Postulation en cours...' : 'Postuler'}
                  </ApplyButton>
                </HousingContent>
              </HousingCard>
            ))}
          </HousingGrid>
        </>
      )}

      {/* Modal de détails du logement */}
      {selectedHousing && (
        <ModalOverlay onClick={() => setSelectedHousing(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{getHousingTitle(selectedHousing)}</ModalTitle>
              <CloseButton onClick={() => setSelectedHousing(null)}>
                <FiX size={24} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Image principale */}
              {getFullImageUrl(selectedHousing.main_photo) && (
                <MainImage
                  src={getFullImageUrl(selectedHousing.main_photo)!}
                  alt={getHousingTitle(selectedHousing)}
                />
              )}

              {/* Galerie d'images */}
              {selectedHousing.housing_photos && selectedHousing.housing_photos.length > 0 && (
                <DetailSection>
                  <DetailTitle>
                    <FiHome />
                    Photos du logement
                  </DetailTitle>
                  <ImageGallery>
                    {selectedHousing.housing_photos.map((photo, index) => (
                      <GalleryImage
                        key={photo.id || index}
                        src={getFullImageUrl(photo.photo_url || photo.photo) || ''}
                        alt={photo.caption || `Photo ${index + 1}`}
                      />
                    ))}
                  </ImageGallery>
                </DetailSection>
              )}

              {/* Informations générales */}
              <DetailSection>
                <DetailTitle>
                  <FiMapPin />
                  Localisation
                </DetailTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailItemLabel>Adresse</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.address || 'Non renseignée'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Ville</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.city || 'Non renseignée'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Code postal</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.postal_code || 'Non renseigné'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Référence</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.reference || 'N/A'}</DetailItemValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {/* Caractéristiques */}
              <DetailSection>
                <DetailTitle>
                  <FiMaximize />
                  Caractéristiques
                </DetailTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailItemLabel>Surface</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.surface ? `${selectedHousing.surface} m²` : 'Non renseignée'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Pièces</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.rooms || 'Non renseigné'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Chambres</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.bedrooms || 'Non renseigné'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Salles de bain</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.bathrooms || 'Non renseigné'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Type</DetailItemLabel>
                    <DetailItemValue>{selectedHousing.type || 'Non renseigné'}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Date de disponibilité</DetailItemLabel>
                    <DetailItemValue>
                      {selectedHousing.available_date
                        ? new Date(selectedHousing.available_date).toLocaleDateString('fr-FR')
                        : 'Immédiate'}
                    </DetailItemValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {/* Informations financières */}
              <DetailSection>
                <DetailTitle>
                  <FiDollarSign />
                  Informations financières
                </DetailTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailItemLabel>Loyer mensuel</DetailItemLabel>
                    <DetailItemValue>{formatPrice(selectedHousing.rent)}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Charges</DetailItemLabel>
                    <DetailItemValue>{formatPrice(selectedHousing.charges)}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Dépôt de garantie</DetailItemLabel>
                    <DetailItemValue>{formatPrice(selectedHousing.deposit)}</DetailItemValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailItemLabel>Total mensuel</DetailItemLabel>
                    <DetailItemValue>
                      {formatPrice((selectedHousing.rent || 0) + (selectedHousing.charges || 0))}
                    </DetailItemValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {/* Description */}
              {selectedHousing.description && (
                <DetailSection>
                  <DetailTitle>Description</DetailTitle>
                  <DescriptionText>{selectedHousing.description}</DescriptionText>
                </DetailSection>
              )}

              {/* Équipements */}
              {selectedHousing.features && selectedHousing.features.length > 0 && (
                <DetailSection>
                  <DetailTitle>Équipements et caractéristiques</DetailTitle>
                  <FeaturesList>
                    {selectedHousing.features.map((feature, index) => (
                      <FeatureItem key={index}>
                        <FiCheckCircle color={theme.colors.success} />
                        {feature}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </DetailSection>
              )}

              {/* Conditions d'attribution */}
              {selectedHousing.conditions && selectedHousing.conditions.length > 0 && (
                <DetailSection>
                  <DetailTitle>Conditions d'attribution</DetailTitle>
                  <FeaturesList>
                    {selectedHousing.conditions.map((condition, index) => (
                      <FeatureItem key={index}>
                        <FiAlertCircle color={theme.colors.primary} />
                        {condition}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </DetailSection>
              )}

              {/* Performance énergétique */}
              {(selectedHousing.energy_class || selectedHousing.ghg_class) && (
                <DetailSection>
                  <DetailTitle>Performance énergétique</DetailTitle>
                  <DetailGrid>
                    {selectedHousing.energy_class && (
                      <DetailItem>
                        <DetailItemLabel>Classe énergétique</DetailItemLabel>
                        <DetailItemValue>{selectedHousing.energy_class}</DetailItemValue>
                      </DetailItem>
                    )}
                    {selectedHousing.ghg_class && (
                      <DetailItem>
                        <DetailItemLabel>Classe GES</DetailItemLabel>
                        <DetailItemValue>{selectedHousing.ghg_class}</DetailItemValue>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </DetailSection>
              )}
            </ModalBody>

            <ModalFooter>
              <PriceDisplay>
                {formatPrice(selectedHousing.rent)}/mois
                {selectedHousing.charges && selectedHousing.charges > 0 && (
                  <div style={{ fontSize: '16px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                    + {selectedHousing.charges.toLocaleString()} FCFA de charges
                  </div>
                )}
              </PriceDisplay>
              <ApplyButtonLarge
                onClick={(e) => handleApply(selectedHousing.id!, e)}
                disabled={applying === selectedHousing.id || !isHousingAvailable(selectedHousing)}
              >
                {applying === selectedHousing.id ? 'Postulation en cours...' : 'Postuler'}
              </ApplyButtonLarge>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardLayout>
  );
};
